import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import './index.css';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import EditorPage from './pages/EditorPage';
import CrmPage from './pages/CrmPage';
import MetaCampaignPage from './pages/MetaCampaignPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ProtectedRoute, { EditorProtectedRoute, AdminProtectedRoute } from './components/ProtectedRoute';
import { LangProvider } from './i18n/LangContext';
import LangPicker from './components/LangPicker';
import Footer from './components/Footer';

function AppFooter() {
  const location = useLocation();
  const hideOn = ['/auth'];
  if (hideOn.some((p) => location.pathname === p)) return null;
  if (location.pathname.startsWith('/editor/')) return null;
  return <Footer />;
}

/**
 * User-facing layout — owns its own language preference under 'lang_user_<email>'
 * so each account remembers its own choice and it never bleeds into admin.
 */
function UserLayout() {
  // Key is per-user-email so two different accounts in the same browser
  // each keep their own language setting.
  const email = localStorage.getItem('userEmail') || '';
  const storageKey = email ? `lang_user_${email}` : 'lang_user';

  return (
    <LangProvider storageKey={storageKey}>
      <Outlet />
      <LangPicker />
      <AppFooter />
    </LangProvider>
  );
}

/**
 * Admin layout — owns its own language preference under 'lang_admin'.
 * Completely independent from user language.
 */
function AdminLayout() {
  return (
    <LangProvider storageKey="lang_admin">
      <Outlet />
    </LangProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ── Admin routes ── isolated language preference */}
        <Route element={<AdminLayout />}>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
        </Route>

        {/* ── User routes ── isolated language preference */}
        <Route element={<UserLayout />}>
          <Route path="/auth" element={<AuthPage />} />
          <Route
            path="/"
            element={(
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/editor/:pageId"
            element={(
              <EditorProtectedRoute>
                <EditorPage />
              </EditorProtectedRoute>
            )}
          />
          <Route
            path="/crm"
            element={(
              <ProtectedRoute>
                <CrmPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/meta"
            element={(
              <AdminProtectedRoute>
                <MetaCampaignPage />
              </AdminProtectedRoute>
            )}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
